import { connect } from "react-redux";
import { browserHistory } from "react-router";
import userActions from "ducks/user";
import entitlementsActions from "ducks/entitlements";
import paymentFormActions from "ducks/paymentForm";
import { setThinking, doneThinking } from "ducks/thinking";
import loginRegister from "ducks/loginRegister";
import UserService from "services/User";
import Entitlements from "services/Entitlements";
import Payments from "services/Payments";
import Subscriptions from "components/v2/screens/Subscriptions";
import appPath from "utils/appPath";
import { addStripeDataToSubscription } from "utils/buildZuoraSubscription";
import { orderSubscriptionsByStatusAndEndDate } from "common/utils/subscriptionHelpers";
import downloadFile from "utils/downloadFile";
import { PAYMENT_METHODS } from "common/constants/Constants";
import REDUX_ERROR_TYPES from "constants/ReduxErrorTypes";
import ERROR_MESSAGES from "common/constants/ErrorMessages";

export function createMapStateToProps() {
    return (state) => {
        const {
            entitlements = {},
            user: { email },
            paymentForm,
            config: { subscriptionOfferUrl, grecaptchaApiKey, bloombergSiteUrl },
            thinking
        } = state;

        const {
            subscriptions = [],
            invoices = [],
            isEditingPayment,
            errors
        } = entitlements;

        const sortedSubscriptions = subscriptions.sort(orderSubscriptionsByStatusAndEndDate);

        return {
            subscriptionOfferUrl,
            subscriptions: sortedSubscriptions,
            invoices,
            email,
            paymentForm,
            thinking,
            isPaypalEnabled: false,
            grecaptchaApiKey,
            isEditingPayment,
            errors,
            bloombergSiteUrl
        };
    };
}

export function mapDispatchToProps(dispatch, { location }) {
    return {
        checkUser() {
            if (!UserService.hasUserToken()) {
                return browserHistory.push(appPath("/login"));
            }

            return dispatch(userActions.startFetchingUser())
                .then((user) => dispatch(loginRegister.setEmail(user.email)))
                .catch((error) => {
                    if (error.status === 401) {
                        browserHistory.push(appPath("/login"));
                    }
                });
        },

        getSubscriptionsData() {
            return dispatch(entitlementsActions.getSubscriptions({
                showAccounts: true,
                showRatePlans: true,
                activeOnly: false,
                bulkSubscriptionActive: false
            }))
                .then((subscriptions) => subscriptions.sort(orderSubscriptionsByStatusAndEndDate))
                .then((subscriptions) => fetchPaymentDetails(subscriptions))
                .then((subscriptions) => {
                    dispatch(entitlementsActions.refreshSubscriptions(subscriptions));

                    if (subscriptions.length > 0) {
                        dispatch(entitlementsActions.getUserInvoices());
                    }
                })
                .then(() => dispatch(doneThinking()))
                .catch(() => dispatch(doneThinking()));
        },

        setThinking(thinkingType) {
            dispatch(setThinking(thinkingType));
        },

        onDownloadInvoice(event, { id: invoiceId, invoiceNumber }) {
            event.preventDefault();

            Entitlements.downloadInvoice(invoiceId)
                .then((r) => r.blob())
                .then((blob) => downloadFile(blob, `${ invoiceNumber }.pdf`))
                .catch(() => dispatch(entitlementsActions.showError({
                    type: REDUX_ERROR_TYPES.FILE_DOWNLOAD,
                    message: ERROR_MESSAGES.ENTITLEMENTS_SHOW_FILE_DOWNLOAD_ERROR
                })));
        },

        clearDownloadError() {
            dispatch(entitlementsActions.clearError(REDUX_ERROR_TYPES.FILE_DOWNLOAD));
        },

        editPayment() {
            dispatch(entitlementsActions.editPayment());
            dispatch(paymentFormActions.clearError());
        },

        submitPayment(subscription, grecaptchaToken) {
            dispatch(setThinking());
            dispatch(paymentFormActions.validatePayment(grecaptchaToken, location.query.skipGrecaptcha))
                .then(() => dispatch(entitlementsActions.updateSubscriptionPaymentMethod(subscription)))
                .then(() => dispatch(setThinking()))
                .then(() => dispatch(entitlementsActions.getSubscriptions({
                    showAccounts: true,
                    showRatePlans: true,
                    activeOnly: false,
                    bulkSubscriptionActive: false
                })))
                .then((subscriptions) => fetchPaymentDetails(subscriptions))
                .then((subscriptions) => dispatch(entitlementsActions.refreshSubscriptions(subscriptions)))
                .then(() => dispatch(doneThinking()))
                .catch(() => dispatch(doneThinking()));
        },

        setPaymentInformation({ creditCard, paypalId }) {
            if (paypalId) {
                dispatch(paymentFormActions.updatePaymentMethod(PAYMENT_METHODS.PAYPAL));
                dispatch(paymentFormActions.updatePayPalId(paypalId));

                return;
            }

            dispatch(paymentFormActions.updatePaymentMethod(PAYMENT_METHODS.STRIPE));
            dispatch(paymentFormActions.updateCardInformation(creditCard));
        }
    };
}

export function fetchPaymentDetails(subscriptions) {
    const promises = subscriptions.map((subscription) => {
        const creditCard = getCreditCard(subscription);

        if (!creditCard) {
            return Promise.resolve(subscription);
        }

        return Payments.getCardFromStripe(creditCard)
            .then((stripeData) => addStripeDataToSubscription(subscription, stripeData))
            .catch(() => Promise.resolve(subscription));
    });

    return Promise.all(promises);
}

function getCreditCard(subscription) {
    return subscription.paymentMethod && subscription.paymentMethod.creditCard;
}

const SubscriptionsContainer = connect(
    createMapStateToProps(),
    mapDispatchToProps
)(Subscriptions);

export default SubscriptionsContainer;
